<template>

  <ThemeSelector class="position-fixed top-0 end-0 p-3"
                 style-checked="background-color: rgb(41, 53, 66)"/>

  <ToastNotification ref="toastNotificationLogin"
                     main-id="login-toast"/>

  <div class="container">

    <div class="login-title">

      <img id="logo"
           alt="Logo"
           width="128"
           height="128"
           :src="logo">

      <h1>
        Welcome to the <span style="white-space: nowrap;">{{ appTitle }}</span>
      </h1>

    </div>

    <div class="row">

      <div class="col-sm-4"></div>

      <div class="col-sm-4">

        <fieldset>

          <div class="form-group"
               style="position: absolute; top: -2048px;">

            <label class="form-label mt-4">
              Email
            </label>

            <input type="text"
                   class="form-control"
                   v-model="email"
                   name="email"
                   autocomplete="off"
                   v-on:keyup.enter="login()"
                   @focus="$event.target.select()"
                   id="email"
                   placeholder="">
          </div>

          <div class="form-group">

            <label class="form-label mt-4">
              Username
            </label>

            <input type="text"
                   class="form-control"
                   v-model="username"
                   v-on:keyup.enter="login()"
                   @focus="$event.target.select()"
                   required
                   id="username"
                   placeholder="">
          </div>

          <div class="form-group">

            <label class="form-label mt-4">
              Password
            </label>

            <input type="password"
                   class="form-control"
                   v-on:keyup.enter="login()"
                   v-model="password"
                   @focus="$event.target.select()"
                   required
                   placeholder="">
          </div>

          <div class="form-group">

            <label class="form-label mt-4">
              Two-Factor Authentication

              <sup>
                <span class="badge rounded-pill bg-primary"
                      id="totpTooltip"
                      type="button"
                      data-bs-toggle="modal"
                      data-bs-target="#totpModal"
                      :title="totpModalText01 + '\n' + totpModalText02">
                  ?
                </span>
              </sup>
            </label>

            <input type="text"
                   class="form-control"
                   v-on:keyup.enter="login()"
                   v-model="totp"
                   @focus="$event.target.select()"
                   placeholder="">
          </div>

          <div class="form-group"
               style="margin-top: 16px;">

            <div class="form-check form-switch">

              <input class="form-check-input"
                     type="checkbox"
                     v-model="rememberMe"
                     @change="onChangeRememberMe"
                     id="flexSwitchRememberMe">

              <label class="form-check-label"
                     for="flexSwitchRememberMe">
                Remember me
              </label>
            </div>

          </div>

          <br/>

          <div class="login-buttons">

            <button type="submit"
                    id="loginButton"
                    class="btn btn-primary"
                    :disabled="loggingIn || (!username && !password)"
                    v-on:click="login();">

            <span class="spinner-border spinner-border-sm text-info"
                  :hidden="!loggingIn"
                  style="margin-right: 3px;"
                  role="status">

              <span class="visually-hidden">
                Logging in...
              </span>

            </span>

              Login

            </button>

            <button class="btn btn-primary btn-passkey-login"
                    type="button"
                    v-if="passkeysSupported"
                    title="Login using a passkey"
                    @click="passkeyLogin()"
                    :disabled="loggingIn">
              <span>🔑</span>
            </button>

            <button class="btn btn-primary btn-oidc-login"
                    type="button"
                    title="Login with Glitched Polygons SSO"
                    @click="loginOIDC()"
                    :disabled="loggingIn">

              <img src="https://static-files.glitchedpolygons.com/glitched-polygons-icon-512x512-blue-variant.png"/>

            </button>

          </div>

        </fieldset>

        <br/>

        <p class="gp-store-link">
          Don't have an account yet?<br/>Acquire one now over on:
          <a href="https://glitchedpolygons.com/store/software/glitched-locale-server">glitchedpolygons.com</a>
        </p>
      </div>

      <div class="col-sm-4"></div>

    </div>

    <!------------------------- MODALS ------------------------->

    <div class="modal fade"
         id="totpModal"
         tabindex="-1"
         aria-labelledby="totpModalLabel"
         aria-hidden="true">

      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">

            <h5 class="modal-title"
                id="totpModalLabel">
              Two-Factor Authentication
            </h5>

            <button type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Dismiss"/>
          </div>

          <div class="modal-body">
            <p>
              {{ totpModalText01 }}
            </p>
            <p>
              {{ totpModalText02 }}
            </p>
          </div>

          <div class="modal-footer">

            <button type="button"
                    class="btn btn-primary"
                    data-bs-dismiss="modal">
              Okay
            </button>

          </div>
        </div>
      </div>
    </div>

    <div class="modal fade"
         id="serverMessageModal"
         tabindex="-1"
         data-bs-keyboard="false"
         data-bs-backdrop="static"
         aria-labelledby="serverMessageModalLabel"
         aria-hidden="true">

      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">

            <h5 class="modal-title"
                id="serverMessageModalLabel">
              Login successful <span>✔️</span>
            </h5>
          </div>

          <div class="modal-body">
            <p>
              Server message:
            </p>
            <pre class="login-server-message">{{ serverMessage }}
            </pre>
          </div>

          <div class="modal-footer">

            <button type="button"
                    class="btn btn-primary"
                    data-bs-dismiss="modal"
                    v-on:click="dismissServerMessage()">
              Continue
            </button>

          </div>
        </div>
      </div>
    </div>

  </div>

  <br/>

</template>

<script>

import Constants from "@/constants";
import ThemeSelector from "@/components/ThemeSelector";
import {sha1} from "@/sha1";
import {getUtcNow} from "@/utc";
import ToastNotification from "@/components/ToastNotification.vue";
import {arrayBufferToBase64URL, arrayBufferToHexEncodedString, decodeBase64URL, dismissServerMessage, encodeBase64Url, handleLoginResponse} from "@/util";
import {Modal} from "bootstrap";

export default {
  name: "Login",
  props: {
    darkTheme: Boolean
  },
  components: {
    ToastNotification,
    ThemeSelector
  },
  data()
  {
    return {
      loggingIn: false,
      rememberMe: false,
      email: "",
      username: "",
      password: "",
      totp: "",
      serverMessage: "",
      passkeysSupported: true,
      totpModalText01: "Enter your 2FA token into this field.",
      totpModalText02: "If you don't have 2FA enabled, you can leave this empty.",
      logo: "/img/logo-dark-theme.png",
      appTitle: process.env.VUE_APP_TITLE,
    }
  },
  mounted()
  {
    document.title = "Glitched Locale Server - Login";

    this.passkeysSupported = typeof PublicKeyCredential !== 'undefined';

    this.emitter.on(Constants.eventNameChangedTheme, darkTheme =>
    {
      this.logo = darkTheme === true ? "/img/logo-dark-theme.png" : "/img/logo-light-theme.png";
    });

    this.logo = localStorage.getItem(Constants.localStorageKeyDarkTheme) === "true" ? "/img/logo-dark-theme.png" : "/img/logo-light-theme.png";

    document.getElementById("username")?.focus();
  },
  methods: {
    clearLoginForm: function (focusUsernameField = true)
    {
      this.username = "";
      this.password = "";
      if (focusUsernameField)
      {
        document.getElementById("username")?.focus();
      }
    },

    onChangeRememberMe: function ()
    {
      // console.log(this.rememberMe ? "\"Remember me\" set to TRUE" : "\"Remember me\" set to FALSE");
    },

    dismissServerMessage: function ()
    {
      dismissServerMessage(this.$router);
    },

    login: function ()
    {
      if (this.loggingIn)
      {
        return;
      }

      this.loggingIn = true;

      const errorMessage = 'Login attempt failed. Please ensure that your credentials are correct or try again later!';

      setTimeout(() =>
      {
        this.email = ""
      }, 128);
      setTimeout(() =>
      {
        fetch(`${process.env.VUE_APP_WEB_API_BASE_URL}/api/v1/login`, {
          method: "POST",
          body: JSON.stringify({
            sessionId: sha1(getUtcNow().toString()),
            email: this.email,
            username: this.username,
            password: this.password,
            totp: this.totp
          }),
          headers: {
            "Content-Type": "application/json; charset=UTF-8"
          }
        }).then(async response =>
        {
          this.loggingIn = false;

          if (response.status !== 200)
          {
            this.clearLoginForm();
            this.$refs.toastNotificationLogin.displayToast('Failure', '❗️', [errorMessage], 4200);
            return;
          }

          const responseEnvelope = await response.json();

          if (!responseEnvelope || !responseEnvelope.items || responseEnvelope.items.length !== 1)
          {
            await this.$router.push("/logout");
            return;
          }

          const loginResponseDto = responseEnvelope.items[0];

          handleLoginResponse(loginResponseDto, this.rememberMe, this.$router, this.loginServerMessageCallback);

        }).catch(() =>
        {
          setTimeout(() =>
          {
            this.clearLoginForm();
            this.loggingIn = false;
            this.$refs.toastNotificationLogin.displayToast('Failure', '❗️', [errorMessage], 4200);
          }, 1337);
        });
      }, 256);
    },

    loginOIDC: async function ()
    {
      if (this.loggingIn)
      {
        return;
      }

      this.loggingIn = true;

      const errorMessage = 'Login attempt failed. Please try again later!';

      try
      {
        const textEncoder = new TextEncoder();

        const entropyResponse = await fetch(`${process.env.VUE_APP_WEB_API_BASE_URL}/api/v1/entropy/sha512`);

        let entropy = await entropyResponse.text();

        if (entropy.length !== 128)
        {
          entropy = arrayBufferToHexEncodedString(await window.crypto.subtle.digest('SHA-512', textEncoder.encode(window.crypto.randomUUID())));
        }

        const state = entropy.slice(0, 64);

        const challenge = entropy.slice(64, 128);

        const challengeSHA256 = encodeBase64Url(await window.crypto.subtle.digest('SHA-256', textEncoder.encode(challenge)));

        localStorage.setItem(Constants.localStorageKeyLoginState, state);

        localStorage.setItem(Constants.localStorageKeyLoginChallenge, challenge);

        localStorage.setItem(Constants.localStorageKeyLoginChallengeS256, challengeSHA256);

        localStorage.setItem(Constants.localStorageKeyRememberMe, this.rememberMe.toString());

        window.location.href = `${process.env.VUE_APP_OIDC_AUTHORITY_BASE_URL}/realms/glitchedpolygons/protocol/openid-connect/auth?client_id=${process.env.VUE_APP_OIDC_CLIENT_ID}&redirect_uri=${encodeURIComponent(process.env.VUE_APP_OIDC_REDIRECT_URI)}&state=${state}&scope=openid+profile+email&response_type=code&response_mode=query&code_challenge_method=S256&code_challenge=${challengeSHA256}`;
      }
      finally
      {
        setTimeout(() =>
        {
          this.clearLoginForm();
          this.loggingIn = false;
          this.$refs.toastNotificationLogin.displayToast('Failure', '❗️', [errorMessage], 4200);
        }, 1337);
      }
    },

    loginServerMessageCallback: function (serverMessage)
    {
      this.serverMessage = serverMessage.replaceAll("<script", "_script");
      new Modal(document.getElementById('serverMessageModal')).show();
    },

    passkeyLogin: async function ()
    {
      if (this.loggingIn)
      {
        return;
      }

      this.loggingIn = true;

      const passkeyAuthFailedErrorMessage = 'Passkey authentication failed. Please login with your main credentials!';

      try
      {
        const response = await fetch(`${process.env.VUE_APP_WEB_API_BASE_URL}/api/v1/login/fido2/passkey`, {
          method: 'POST'
        });

        if (!response.ok)
        {
          this.$refs.toastNotificationLogin.displayToast('Error', '⚠️', ['Passkey authentication failed. Please login with your main credentials!'], 4200);
          return;
        }

        const responseBodyEnvelope = await response.json();

        let options = responseBodyEnvelope.items[0];

        options.challenge = decodeBase64URL(options.challenge).buffer;

        let assertion;

        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: decodeBase64URL(cred.id).buffer
        }));

        try
        {
          assertion = await navigator.credentials.get({publicKey: options});
        }
        catch (e)
        {
          this.$refs.toastNotificationLogin.displayToast('Error', '😖', [passkeyAuthFailedErrorMessage], 4200);
          return;
        }

        if (!assertion)
        {
          this.$refs.toastNotificationLogin.displayToast('Error', '❗️', [passkeyAuthFailedErrorMessage], 4200);
          return;
        }

        const assertionResponse = {
          id: assertion.id,
          rawId: arrayBufferToBase64URL(assertion.rawId),
          type: assertion.type,
          clientExtensionResults: {},
          response: {
            authenticatorData: arrayBufferToBase64URL(assertion.response.authenticatorData),
            clientDataJSON: arrayBufferToBase64URL(assertion.response.clientDataJSON),
            signature: arrayBufferToBase64URL(assertion.response.signature),
            userHandle: assertion.response.userHandle ? arrayBufferToBase64URL(assertion.response.userHandle) : null
          }
        };

        const verificationResponse = await fetch(`${process.env.VUE_APP_WEB_API_BASE_URL}/api/v1/login/fido2/passkey/verify`, {
          method: 'POST',
          body: JSON.stringify(assertionResponse),
          headers: {
            'Content-Type': 'application/json',
            'X-FIDO2-ChallengeBase64URL': arrayBufferToBase64URL(options.challenge)
          }
        });

        if (!verificationResponse.ok)
        {
          this.$refs.toastNotificationLogin.displayToast('Error', '❌', [passkeyAuthFailedErrorMessage], 4200);
          return;
        }

        const verificationResponseBodyEnvelope = await verificationResponse.json();

        if (!verificationResponseBodyEnvelope.items || verificationResponseBodyEnvelope.items.length !== 1 || verificationResponseBodyEnvelope.type !== 'LoginResponseDto')
        {
          this.$refs.toastNotificationLogin.displayToast('Error', '🔴', [passkeyAuthFailedErrorMessage], 4200);
        }
        else
        {
          handleLoginResponse(verificationResponseBodyEnvelope.items[0], this.rememberMe, this.$router, this.loginServerMessageCallback);
        }
      }
      catch
      {
        this.$refs.toastNotificationLogin.displayToast('Error', '💥', [passkeyAuthFailedErrorMessage], 4200);
      }
      finally
      {
        this.loggingIn = false;
      }
    }
  },
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

#logo {
  margin-top: 64px;
}

.login-title {
  text-align: center;
}

#totpTooltip {
  margin-left: 4px;
  scale: 96%;
  cursor: help;
  font-weight: bold;
}

#loginButton {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  float: right;
}

@media (max-width: 768px) {
  #loginButton {
    width: 100%;
  }
}

.login-server-message {
  padding: 16px;
  overflow-y: auto;
  max-height: 150px;
  border-radius: 6px;
  white-space: pre-line;
}

.container {
  padding-bottom: 16px;
}

.gp-store-link {
  opacity: 50%;
  text-align: right;
  font-size: 0.825rem;
}

@media (max-width: 768px) {
  .gp-store-link {
    text-align: center;
  }
}

.login-buttons {
  gap: 8px;
  display: flex;
  justify-content: end;
}

.btn-oidc-login,
.btn-passkey-login {
  padding-left: 25px;
  padding-right: 25px;
  display: flex;
  align-items: center;
}

.btn-oidc-login > img {
  width: 20px;
  height: 20px;
}

</style>
